import { DEFAULT_REGION_CODE, REGIONS } from '@/data'
import { getFallbackRegionsByRegion } from '@/utils/regions'
import cmsSettings from '../cmsSettings'
import menuItemCategory from '../menuItemCategory'

// `$language` reaches GROQ with underscores (en_ca); the fallback util uses hyphens (en-ca).
const toHyphen = (regionCode: string) => regionCode.replace('_', '-')
const toUnderscore = (region: string) => region.replace('-', '_')

// Resolves each item's allergen block as a whole (never merged allergen by allergen) through the
// site's region fallback chain. `en_us` always qualifies; any other region's block counts only
// when its `override` toggle is on.
export const buildAllergensSelect = () => {
  const regionBranches = Object.values(REGIONS)
    .map(toUnderscore)
    .filter(code => code !== DEFAULT_REGION_CODE)
    .map(code => {
      const chain = getFallbackRegionsByRegion(toHyphen(code)).map(toUnderscore)
      const candidates = chain
        .filter(region => region !== DEFAULT_REGION_CODE)
        .map(region => `allergens.${region}.override == true => allergens.${region}`)
      return `$language == "${code}" => select(${[...candidates, `allergens.${DEFAULT_REGION_CODE}`].join(', ')})`
    })

  return `select(${[...regionBranches, `allergens.${DEFAULT_REGION_CODE}`].join(', ')})`
}

// Menu items opt in with `allergens.showInGuide`, so adding a dish to the guide needs no page
// edit. Category and title follow the menu item's own region data (falling back to the default
// region); items are sorted by the title the visitor sees.
const regionCategoryRef = `coalesce(menuItemData.i18n[$language].category._ref, menuItemData.i18n.${DEFAULT_REGION_CODE}.category._ref)`

export const fields = `
  _type,
  _id,
  ${cmsSettings()},
  sectionBackground,
  disclaimer,
  legendText,
  categories[]{
    categoryReference->{${menuItemCategory.fields}},
    "items": *[_type == "menuItem" && allergens.showInGuide == true && $language in enabledRegions && ${regionCategoryRef} == ^.categoryReference._ref] {
      _id,
      "title": coalesce(menuItemData.i18n[$language].title, title),
      "allergens": ${buildAllergensSelect()}
    } | order(title asc)
  },
`

export const fragment = (name = 'allergenGuide') => `${name}{ ${fields} }`

const exported = {
  fields,
  fragment,
}

export default exported
